/**
 * Date formatting utilities for consistent display
 */

import dayjs from 'dayjs';
import { parseDate } from './core';

/**
 * Formats a date string for display
 * 
 * @param dateString The date string to format
 * @param formatPattern The desired output format
 * @returns A formatted date string or a fallback message if the date is invalid
 */
export function formatDateString(dateString: string | null | undefined, formatPattern: string = 'MMM D, YYYY h:mm A'): string {
  if (!dateString) return 'No date';
  
  const date = parseDate(dateString);
  if (!date) return 'Invalid date';
  
  return date.format(formatPattern);
}

/**
 * Formats a date for use in a datetime-local input
 * Converts to YYYY-MM-DDTHH:MM format without timezone
 * 
 * @param dateString The date string to format for input
 * @returns Formatted string suitable for datetime-local input
 */
export function formatForDateTimeInput(dateString: string | null | undefined): string {
  if (!dateString) return '';
  
  const date = parseDate(dateString);
  if (!date) return '';
  
  // Format to YYYY-MM-DDTHH:MM format for datetime-local inputs
  return date.format('YYYY-MM-DDTHH:mm');
}

/**
 * Format a date for date input field
 * Format: YYYY-MM-DD
 */
export function formatDateForInput(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Format a time for time input field
 * Format: HH:mm
 */
export function formatTimeForInput(date: Date): string {
  return dayjs(date).format('HH:mm');
}

/**
 * Formats a date object while preserving local timezone information
 * This is crucial for storing dates in the database that accurately reflect
 * the user's local time without converting to UTC (which happens with toISOString())
 * 
 * @param date The date object to format
 * @returns A formatted date string with timezone offset for PostgreSQL compatibility
 */
export function formatDatePreservingTimezone(date: Date): string {
  const pad = (num: number) => String(num).padStart(2, '0');
  
  // Get timezone offset in minutes and convert to hours and minutes
  const tzOffset = date.getTimezoneOffset();
  const tzOffsetHours = Math.floor(Math.abs(tzOffset) / 60);
  const tzOffsetMinutes = Math.abs(tzOffset) % 60;
  
  // Format timezone offset as +/-HH:MM
  // Note: getTimezoneOffset() returns negative value for positive UTC offsets
  // and positive for negative UTC offsets, so we need to invert the sign
  const tzSign = tzOffset <= 0 ? '+' : '-';
  const tzFormatted = `${tzSign}${pad(tzOffsetHours)}:${pad(tzOffsetMinutes)}`;
  
  // Format the date with timezone information
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
         `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${tzFormatted}`;
}

/**
 * Formats a date string according to the specified format
 * 
 * @param dateString The date string to format
 * @param format The format string to use (defaults to 'YYYY-MM-DD')
 * @returns A formatted date string
 */
export function formatDate(dateString: string | Date, format: string = 'YYYY-MM-DD'): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Handle invalid dates
  if (isNaN(date.getTime())) return '';
  
  const pad = (num: number) => String(num).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  
  // Replace format tokens with actual values
  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
}

/**
 * Formats a date for a user-friendly display in activity tables
 * Includes day of week and a more readable date format
 * 
 * @param dateString The date string to format
 * @param timezone Optional timezone to format in
 * @returns A nicely formatted date string for table display
 */
export function formatDateForActivityTable(dateString: string | Date, timezone?: string): string {
  if (!dateString) return 'No date';
  
  const date = typeof dateString === 'string' ? parseDate(dateString) : dayjs(dateString);
  if (!date) return 'Invalid date';
  
  // Format with day of week, full month name, and 12-hour time with AM/PM
  return date.format('ddd, MMM D, YYYY [at] h:mm A');
}

/**
 * Returns a relative time string (e.g., "2 hours ago", "yesterday") for a date
 * 
 * @param date The date to get relative time for
 * @returns A relative time string
 */
export function getRelativeTimeFromDate(date: Date): string {
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  } else {
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  }
}

/**
 * Returns a relative time string (e.g., "2 hours ago", "yesterday") for a database timestamp
 * 
 * @param dbTimestamp The timestamp string from the database
 * @returns A relative time string in the user's local timezone
 */
export function getRelativeTimeFromDbTimestamp(dbTimestamp: string | null): string {
  if (!dbTimestamp) return '';
  
  const localDate = convertDbTimestampToLocalDate(dbTimestamp);
  if (!localDate) return '';
  
  return getRelativeTimeFromDate(localDate);
}

/**
 * Converts a database timestamp string to a Date object in the user's local timezone
 * To be used when displaying dates retrieved from the database
 * 
 * @param dbTimestamp The timestamp string from the database
 * @returns A Date object in the user's local timezone
 */
export function convertDbTimestampToLocalDate(dbTimestamp: string | null): Date | null {
  if (!dbTimestamp) return null;
  
  // Parse the timestamp string into a Date object
  // This will automatically convert it to the local timezone
  return new Date(dbTimestamp);
}
